import React, { useState } from 'react';

export type KanbanProposal = Record<string, any>;
export type ExecuteProposalRequest = Record<string, any>;

export interface KanbanProposalCardProps {
  proposal: any;
  onApprove: (request: any) => Promise<void>;
  onReject: (proposalId: string) => void;
  onInspectContext?: (ctx: { type: 'task' | 'wiki'; id: string; title: string }) => void;
}

export const KanbanProposalCard: React.FC<KanbanProposalCardProps> = ({
  proposal,
  onApprove,
  onReject,
  onInspectContext,
}) => {
  const [isExecuting, setIsExecuting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const pid = proposal.id || proposal.proposalId || '';
  const displayTitle = proposal.title || proposal.reason || 'Proposal';
  const isPending = proposal.status === 'pending';
  const isExpired = Date.now() > proposal.expiresAt || proposal.status === 'expired';

  const handleApproveClick = async () => {
    setIsExecuting(true);
    setErrorMsg(null);
    try {
      await onApprove({ proposalId: pid, approved: true });
    } catch (err: any) {
      setErrorMsg(err?.message || 'Approval failed');
    } finally {
      setIsExecuting(false);
    }
  };

  return (
    <div
      style={{
        padding: '12px',
        borderRadius: '6px',
        backgroundColor: 'var(--theme-bg-surface, #1e293b)',
        border: '1px solid var(--theme-border, rgba(255,255,255,0.1))',
        marginBottom: '8px',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
        <span style={{ fontWeight: 600, color: 'var(--theme-text-primary, #f8fafc)' }}>
          {displayTitle}
        </span>
        <span
          style={{
            fontSize: '11px',
            padding: '2px 6px',
            borderRadius: '4px',
            backgroundColor: isPending ? '#d97706' : '#475569',
            color: '#fff',
          }}
        >
          {proposal.status}
        </span>
      </div>

      {proposal.summary && (
        <p style={{ fontSize: '13px', color: 'var(--theme-text-secondary, #94a3b8)', margin: '4px 0 8px 0' }}>
          {proposal.summary}
        </p>
      )}

      {errorMsg && (
        <div style={{ color: '#ef4444', fontSize: '12px', marginBottom: '8px' }}>
          {errorMsg}
        </div>
      )}

      <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
        {isPending && !isExpired && (
          <>
            <button
              onClick={handleApproveClick}
              disabled={isExecuting}
              style={{
                padding: '4px 10px',
                fontSize: '12px',
                borderRadius: '4px',
                backgroundColor: '#10b981',
                color: '#fff',
                border: 'none',
                cursor: isExecuting ? 'not-allowed' : 'pointer',
              }}
            >
              {isExecuting ? 'Executing...' : 'Approve'}
            </button>
            <button
              onClick={() => onReject(pid)}
              disabled={isExecuting}
              style={{
                padding: '4px 10px',
                fontSize: '12px',
                borderRadius: '4px',
                backgroundColor: '#ef4444',
                color: '#fff',
                border: 'none',
                cursor: isExecuting ? 'not-allowed' : 'pointer',
              }}
            >
              Reject
            </button>
          </>
        )}

        {onInspectContext && (
          <button
            onClick={() => onInspectContext({ type: 'task', id: pid, title: displayTitle })}
            style={{
              padding: '4px 10px',
              fontSize: '12px',
              borderRadius: '4px',
              backgroundColor: 'transparent',
              color: 'var(--theme-accent, #60a5fa)',
              border: '1px solid var(--theme-border, #334155)',
              cursor: 'pointer',
              marginLeft: 'auto',
            }}
          >
            Inspect
          </button>
        )}
      </div>
    </div>
  );
};
